import { getStableUserKey } from '../identity/ZionIdentityManager.ts'
import { markSessionUnlocked } from '../identity/ZionIdentityManager.ts'
import {
  getMasterPasswordHash,
  getMasterPasswordKdfRecord,
  getNickBase,
  getNickNumber,
  getPrivateKeyBase64,
  getPublicKeyBase64,
  importIdentityFromBackup,
} from '../storage/keyStore.ts'
import { replaceAllContacts, listContacts, upsertPairedContact, type ZionContact } from '../storage/pairingStore.ts'
import {
  replaceAllMessages,
  loadMessages,
  appendMessage,
  getAllMessages,
  type ChatMessage,
} from '../storage/chatStore.ts'
import {
  getDailyCode,
  getInboundQueueId,
  getSessionToken,
  getQueueOwnerPubKey,
  saveDailyCode,
  saveInboundQueueId,
  saveQueueOwnerPubKey,
  saveSessionToken,
} from '../storage/sessionStore.ts'
import { serializeMasterPasswordKdf } from '../crypto/pbkdf2Kdf.ts'
import { getCurrentTheme, parseThemeId, applyTheme, type ZionThemeId } from '../ui/themes.ts'
import { BACKUP_FORMAT, BACKUP_VERSION } from '../backup/ZionBackup.ts'
import {
  defaultDeviceLabel,
  detectPlatform,
  getLocalDeviceId,
  registerSelfDevice,
  saveDeviceLinkKeyB64,
  upsertLinkedDevice,
  type LinkedDevice,
} from './deviceLinkStore.ts'

export const DEVICE_VAULT_FORMAT = 'zion-device-vault-v1'

interface BackupMessageWire {
  id: string | number
  peer?: string
  text: string
  outgoing: boolean
  at: number
  pending?: boolean
  replyToId?: string
  replyPreview?: string
  mediaKind?: string
  mediaMime?: string
  mediaFileName?: string
  mediaGzip?: boolean
  mediaOrigSize?: number
}

export interface DeviceVaultPayload {
  format: typeof DEVICE_VAULT_FORMAT
  version: 1
  exportedAt: number
  deviceLinkKeyB64: string
  senderDevice: LinkedDevice
  vault: Record<string, unknown>
}

function wireFromMessage(m: ChatMessage): BackupMessageWire {
  return {
    id: m.id,
    peer: m.contactId,
    text: m.text,
    outgoing: m.outgoing,
    at: m.timestamp,
    pending: m.pending,
    replyToId: m.replyToId,
    replyPreview: m.replyPreview,
    mediaKind: m.mediaKind,
    mediaMime: m.mediaMime,
    mediaFileName: m.mediaFileName,
    mediaGzip: m.mediaGzip,
    mediaOrigSize: m.mediaOrigSize,
  }
}

function wireToChatMessage(peer: string, row: BackupMessageWire): ChatMessage {
  const id =
    typeof row.id === 'string' && row.id.trim() ? row.id.trim() : String(row.at || Date.now())
  return {
    id,
    contactId: (row.peer ?? peer).trim(),
    text: row.text,
    outgoing: !!row.outgoing,
    timestamp: row.at || Date.now(),
    pending: row.pending,
    replyToId: row.replyToId,
    replyPreview: row.replyPreview,
    mediaKind: row.mediaKind,
    mediaMime: row.mediaMime,
    mediaFileName: row.mediaFileName,
    mediaGzip: row.mediaGzip,
    mediaOrigSize: row.mediaOrigSize,
  }
}

function parseContact(raw: unknown): ZionContact | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const contactId = String(o.contactId ?? '').trim()
  const publicKey = String(o.publicKey ?? '').trim()
  const sessionToken = String(o.sessionToken ?? '').trim()
  if (!contactId || !publicKey || !sessionToken) return null
  return {
    contactId,
    publicKey,
    sessionToken,
    nickname: String(o.nickname ?? '').trim(),
    pairedAt: Number(o.pairedAt) || Date.now(),
    inboundQueueId: String(o.inboundQueueId ?? '').trim(),
    customName: String(o.customName ?? '').trim() || undefined,
  }
}

async function buildInnerVault(theme: ZionThemeId): Promise<Record<string, unknown>> {
  const userKey = (await getStableUserKey()) || 'zion'
  const contacts = await listContacts()
  const chats: Record<string, BackupMessageWire[]> = {}
  let messageCount = 0

  for (const c of contacts) {
    const msgs = await loadMessages(c.contactId)
    if (!msgs.length) continue
    chats[c.contactId] = msgs.map(wireFromMessage)
    messageCount += msgs.length
  }

  const [sessionToken, inboundQueueId, dailyCode, nickBase, nickNumber] = await Promise.all([
    getSessionToken(),
    getInboundQueueId(),
    getDailyCode(),
    getNickBase(),
    getNickNumber(),
  ])
  const [privateKey, publicKey, masterKdf, masterHashLegacy] = await Promise.all([
    getPrivateKeyBase64(),
    getPublicKeyBase64(),
    getMasterPasswordKdfRecord(),
    getMasterPasswordHash(),
  ])
  const queueOwnerPubKey = await getQueueOwnerPubKey()

  return {
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    user: userKey,
    exportedAt: Date.now(),
    chats,
    contacts: {},
    pairingContacts: contacts.map((c) => ({
      contactId: c.contactId,
      publicKey: c.publicKey,
      nickname: c.nickname,
      pairedAt: c.pairedAt,
      sessionToken: c.sessionToken,
      inboundQueueId: c.inboundQueueId,
      customName: c.customName ?? '',
    })),
    identity: {
      sessionToken: sessionToken ?? '',
      inboundQueueId: inboundQueueId ?? '',
      nickname: nickBase && nickNumber ? `${nickBase}#${nickNumber}` : userKey,
      dailyCode: dailyCode ?? '',
      queueOwnerPubKey: queueOwnerPubKey ?? '',
    },
    webKeys: {
      privateKey: privateKey ?? '',
      publicKey: publicKey ?? '',
      nickBase: nickBase ?? '',
      nickNumber: String(nickNumber || 0),
      masterPasswordKdf: masterKdf ? serializeMasterPasswordKdf(masterKdf) : '',
      masterPasswordHash: masterKdf ? '' : (masterHashLegacy ?? ''),
    },
    theme,
    messageCount,
  }
}

/** Dump completo IndexedDB per pairing multi-device. */
export async function buildDeviceVaultPayload(deviceLinkKeyB64: string): Promise<DeviceVaultPayload> {
  const theme = getCurrentTheme()
  const senderDevice = await registerSelfDevice()
  return {
    format: DEVICE_VAULT_FORMAT,
    version: 1,
    exportedAt: Date.now(),
    deviceLinkKeyB64,
    senderDevice,
    vault: await buildInnerVault(theme),
  }
}

/** Patch cronologia (Fase 3) — solo chat/contatti aggiornati. */
export async function buildHistoryPatchPayload(): Promise<Record<string, unknown>> {
  const contacts = await listContacts()
  const chats: Record<string, BackupMessageWire[]> = {}
  for (const c of contacts) {
    const msgs = await loadMessages(c.contactId)
    if (msgs.length) chats[c.contactId] = msgs.map(wireFromMessage)
  }
  return {
    format: 'zion-history-patch-v1',
    exportedAt: Date.now(),
    pairingContacts: contacts.map((c) => ({
      contactId: c.contactId,
      publicKey: c.publicKey,
      nickname: c.nickname,
      pairedAt: c.pairedAt,
      sessionToken: c.sessionToken,
      inboundQueueId: c.inboundQueueId,
      customName: c.customName ?? '',
    })),
    chats,
  }
}

async function applyVaultRoot(root: Record<string, unknown>, merge: boolean): Promise<{
  contactCount: number
  messageCount: number
}> {
  const pairingArr = root.pairingContacts
  const contacts: ZionContact[] = []
  if (Array.isArray(pairingArr)) {
    for (const item of pairingArr) {
      const c = parseContact(item)
      if (c) contacts.push(c)
    }
  }

  const chatsObj = root.chats
  const incoming: ChatMessage[] = []
  if (chatsObj && typeof chatsObj === 'object') {
    for (const [peer, arr] of Object.entries(chatsObj as Record<string, unknown>)) {
      if (!Array.isArray(arr)) continue
      for (const item of arr) {
        if (!item || typeof item !== 'object') continue
        const row = item as BackupMessageWire
        if (!row.text && !row.mediaKind) continue
        incoming.push(wireToChatMessage(peer, row))
      }
    }
  }

  if (!merge) {
    const webKeys = root.webKeys as Record<string, string> | undefined
    if (webKeys?.privateKey && webKeys.publicKey) {
      await importIdentityFromBackup({
        privateKeyBase64: webKeys.privateKey,
        publicKeyBase64: webKeys.publicKey,
        nickBase: webKeys.nickBase,
        nickNumber: parseInt(webKeys.nickNumber ?? '0', 10),
        masterPasswordKdf: String(webKeys.masterPasswordKdf ?? ''),
        masterPasswordHash: webKeys.masterPasswordHash,
      })
      markSessionUnlocked()
    }

    const identity = root.identity as Record<string, string> | undefined
    if (identity) {
      if (identity.sessionToken?.trim()) await saveSessionToken(identity.sessionToken.trim())
      if (identity.inboundQueueId?.trim()) await saveInboundQueueId(identity.inboundQueueId.trim())
      if (identity.dailyCode?.trim()) await saveDailyCode(identity.dailyCode.trim())
      if (identity.queueOwnerPubKey?.trim()) await saveQueueOwnerPubKey(identity.queueOwnerPubKey.trim())
    }

    if (contacts.length) await replaceAllContacts(contacts)
    if (incoming.length) await replaceAllMessages(incoming)

    const theme = parseThemeId(String(root.theme ?? ''))
    applyTheme(theme)
    return { contactCount: contacts.length, messageCount: incoming.length }
  }

  for (const c of contacts) {
    await upsertPairedContact(
      {
        sessionToken: c.sessionToken,
        publicKey: c.publicKey,
        nickname: c.nickname,
        inboundQueueId: c.inboundQueueId,
      },
      { refreshPairedAt: false },
    )
  }

  const existing = await getAllMessages()
  const byId = new Map(existing.map((m) => [m.id, m]))
  for (const m of incoming) {
    if (!byId.has(m.id)) {
      await appendMessage(m)
      byId.set(m.id, m)
    }
  }

  return { contactCount: contacts.length, messageCount: incoming.length }
}

/** Import vault completo dopo pairing (nuovo dispositivo). */
export async function applyDeviceVaultPayload(payload: DeviceVaultPayload): Promise<void> {
  if (payload.format !== DEVICE_VAULT_FORMAT) throw new Error('formato_vault_non_valido')
  await saveDeviceLinkKeyB64(payload.deviceLinkKeyB64)
  await applyVaultRoot(payload.vault, false)

  const localId = await getLocalDeviceId()
  await registerSelfDevice(defaultDeviceLabel(detectPlatform()))
  await upsertLinkedDevice({ ...payload.senderDevice, isSelf: false })
  await upsertLinkedDevice({
    deviceId: localId,
    label: defaultDeviceLabel(),
    platform: detectPlatform(),
    linkedAt: Date.now(),
    isSelf: true,
  })
}

/** Merge patch cronologia (Fase 3). */
export async function applyHistoryPatch(root: Record<string, unknown>): Promise<number> {
  const r = await applyVaultRoot(root, true)
  return r.messageCount
}
